<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  // [{ value, label, dotClass? }] — dotClass draws a colour marker, like statuses
  options: { type: Array, required: true },
  placeholder: { type: String, default: '' },
  // 'sm' matches the compact filters above the dialog list
  size: { type: String, default: 'md' },
  disabled: { type: Boolean, default: false },
  fullWidth: { type: Boolean, default: false },
  // Adds a filter field to the menu: for lists too long to scan by eye
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

// The id/label attributes belong to the button, not to the wrapper
defineOptions({ inheritAttrs: false })

const open = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const searchRef = ref(null)
const query = ref('')
const menuStyle = ref({})
const activeIndex = ref(-1)

const visibleOptions = computed(() => {
  const needle = query.value.trim().toLowerCase()
  if (!props.searchable || !needle) return props.options

  return props.options.filter(option =>
    option.label.toLowerCase().includes(needle)
  )
})

const selected = computed(() =>
  props.options.find(option => option.value === props.modelValue)
)
const label = computed(() => selected.value?.label || props.placeholder)

const triggerClass = computed(() => [
  props.size === 'sm'
    ? 'h-6 pl-1.5 pr-1 text-xs gap-1'
    : 'h-8 px-2 text-sm gap-2',
  props.fullWidth ? 'w-full' : '',
])

// The menu is teleported and positioned by the trigger, so scrollable
// containers (the monitoring table, the dialog list) cannot clip it
const placeMenu = () => {
  const rect = triggerRef.value?.getBoundingClientRect()
  if (!rect) return

  menuStyle.value = {
    left: `${rect.left}px`,
    top: `${rect.bottom + 4}px`,
    minWidth: `${rect.width}px`,
  }
}

const close = () => {
  open.value = false
  activeIndex.value = -1
  query.value = ''
}

const toggle = async () => {
  if (props.disabled) return
  open.value = !open.value
  if (!open.value) return

  placeMenu()
  activeIndex.value = visibleOptions.value.findIndex(
    option => option.value === props.modelValue
  )
  await nextTick()
  searchRef.value?.focus()
  menuRef.value
    ?.querySelector('[data-active="true"]')
    ?.scrollIntoView({ block: 'nearest' })
}

const pick = option => {
  emit('update:modelValue', option.value)
  close()
  triggerRef.value?.focus()
}

const onKeydown = event => {
  if (props.disabled) return

  if (!open.value) {
    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      event.preventDefault()
      toggle()
    }
    return
  }

  const lastIndex = visibleOptions.value.length - 1
  const keys = {
    Escape: () => close(),
    ArrowDown: () => {
      activeIndex.value = Math.min(activeIndex.value + 1, lastIndex)
    },
    ArrowUp: () => {
      activeIndex.value = Math.max(activeIndex.value - 1, 0)
    },
    Home: () => {
      activeIndex.value = 0
    },
    End: () => {
      activeIndex.value = lastIndex
    },
    Enter: () => {
      const option = visibleOptions.value[activeIndex.value]
      if (option) pick(option)
    },
  }
  const handler = keys[event.key]
  if (!handler) return

  event.preventDefault()
  handler()
  nextTick(() => {
    menuRef.value
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  })
}

const onDocumentClick = event => {
  if (!open.value) return
  if (triggerRef.value?.contains(event.target)) return
  if (menuRef.value?.contains(event.target)) return
  close()
}

// The menu follows the trigger while the page scrolls under it
watch(open, isOpen => {
  if (isOpen) {
    window.addEventListener('scroll', placeMenu, true)
    window.addEventListener('resize', placeMenu)
  } else {
    window.removeEventListener('scroll', placeMenu, true)
    window.removeEventListener('resize', placeMenu)
  }
})

// Lets callers focus the control, e.g. when a modal opens
defineExpose({ focus: () => triggerRef.value?.focus() })

onMounted(() => document.addEventListener('click', onDocumentClick))
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  window.removeEventListener('scroll', placeMenu, true)
  window.removeEventListener('resize', placeMenu)
})
</script>

<template>
  <div :class="fullWidth ? 'w-full' : 'inline-flex'">
    <button
      v-bind="$attrs"
      ref="triggerRef"
      type="button"
      class="flex items-center justify-between border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-brand disabled:opacity-50"
      :class="triggerClass"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :disabled="disabled"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="flex items-center gap-1.5 min-w-0">
        <span
          v-if="selected?.dotClass"
          class="rounded-full size-2 shrink-0"
          :class="selected.dotClass"
        />
        <span class="truncate" :class="{ 'text-n-slate-11': !selected }">
          {{ label }}
        </span>
      </span>
      <span
        class="shrink-0 text-n-slate-11"
        :class="[
          open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
          size === 'sm' ? 'size-3' : 'size-4',
        ]"
      />
    </button>

    <teleport v-if="open" to="body">
      <ul
        ref="menuRef"
        role="listbox"
        class="fixed z-50 p-1 m-0 overflow-y-auto list-none max-h-72 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-sm"
        :style="menuStyle"
      >
        <li v-if="searchable" class="p-1">
          <input
            ref="searchRef"
            v-model="query"
            type="text"
            class="w-full h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            :placeholder="searchPlaceholder"
            @keydown="onKeydown"
            @input="activeIndex = 0"
          />
        </li>
        <li v-for="(option, index) in visibleOptions" :key="option.value">
          <button
            type="button"
            role="option"
            :aria-selected="option.value === modelValue"
            :data-active="index === activeIndex"
            class="flex items-center w-full gap-2 p-2 text-sm text-left border-0 rounded-lg cursor-pointer text-n-slate-12"
            :class="
              index === activeIndex
                ? 'bg-n-alpha-2'
                : 'bg-transparent hover:bg-n-alpha-2'
            "
            @click="pick(option)"
            @mousemove="activeIndex = index"
          >
            <span
              v-if="option.dotClass"
              class="rounded-full size-2 shrink-0"
              :class="option.dotClass"
            />
            <span class="truncate">{{ option.label }}</span>
            <span
              v-if="option.value === modelValue"
              class="ml-auto i-lucide-check size-3.5 text-n-brand shrink-0"
            />
          </button>
        </li>
      </ul>
    </teleport>
  </div>
</template>
